import { useStore } from 'react-redux';

import getWholeMonthData from '../../selectors/wholeMonthData';
import { trackInteractionEvent } from '../logging/trackInteractionEvent';

import type { State } from '../../selectors/types';

type PriceGridData = {
  price: number | null;
  directAvailable: boolean | undefined;
  date: Date;
};

type PriceStats = {
  populatedRatioPercent: number;
  meanPrice?: number;
  minPrice?: number;
};

// tallyman.InteractionEvent's `numbers` field only accepts integers, so
// prices are rounded to whole pounds and the ratio to a whole percentage.
const computePriceStats = (gridData: PriceGridData[]): PriceStats => {
  const priceArray = gridData.map((day) => day.price);
  const nonNullPrices = priceArray.filter(
    (price): price is number => price !== null,
  );
  const populatedRatioPercent = Math.round(
    (priceArray.length > 0 ? nonNullPrices.length / priceArray.length : 0) *
      100,
  );

  if (nonNullPrices.length === 0) {
    return { populatedRatioPercent };
  }

  const meanPrice =
    nonNullPrices.reduce((sum, price) => sum + price, 0) / nonNullPrices.length;
  const minPrice = Math.min(...nonNullPrices);

  return {
    populatedRatioPercent,
    meanPrice: Math.round(meanPrice),
    minPrice: Math.round(minPrice),
  };
};

const buildEventNumbers = (before: PriceStats, after: PriceStats) => ({
  initialPopulatedRatioPercent: before.populatedRatioPercent,
  ...(before.meanPrice !== undefined && {
    initialMeanPrice: before.meanPrice,
  }),
  ...(before.minPrice !== undefined && { initialMinPrice: before.minPrice }),
  populatedRatioPercent: after.populatedRatioPercent,
  ...(after.meanPrice !== undefined && { meanPrice: after.meanPrice }),
  ...(after.minPrice !== undefined && { minPrice: after.minPrice }),
});

export const useDepartureTimesFilterTracking = () => {
  const store = useStore<State>();

  const trackFilterChange = (applyFilterChange: () => void) => {
    const before = computePriceStats(
      getWholeMonthData(store.getState(), 'outbound'),
    );

    applyFilterChange();

    const after = computePriceStats(
      getWholeMonthData(store.getState(), 'outbound'),
    );

    trackInteractionEvent({
      name: 'Departure_Time_Filter_Changed',
      numbers: buildEventNumbers(before, after),
    });
  };

  return { trackFilterChange };
};
